import { createFileRoute } from "@tanstack/react-router";
import { L, LegalAside, LegalPage, LegalSection, LList } from "@/components/LegalPage";

export const Route = createFileRoute("/privacy")({
  head: () => {
    const title = "Privacy — WHO KNEW";
    const description =
      "What WHO KNEW does with your data. No selling, no sharing with advertisers, no tracking across other sites.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/privacy" },
      ],
    };
  },
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalPage eyebrow="PRIVACY" title="What we do with your data.">
      <LegalSection label="WHO RUNS THIS" first>
        <L>
          <span className="text-burgundy">WHO KNEW</span> is operated as a business based in the United Kingdom.
        </L>
        <L>
          If you need to contact us about your data, reach us at hello@whoknew.guide.
        </L>
      </LegalSection>

      <LegalSection label="WHAT WE COLLECT">
        <L>When you buy a product or sign up for updates, we collect:</L>
        <LList
          items={[
            "Your email address.",
            "Your name, if you give it to us (you usually don't have to).",
            "Information about how you've used the products you've bought — which lessons you've watched, which materials you've downloaded — so we can show your progress when you come back.",
            "Payment information, which is handled entirely by Stripe. We never see your card details.",
          ]}
        />
        <L>When you visit the site, we collect:</L>
        <LList
          items={[
            "Basic anonymous analytics (which pages you visited, how long you stayed). No tracking across other sites. No selling this on to anyone.",
          ]}
        />
      </LegalSection>

      <LegalSection label="WHAT WE DON'T DO">
        <L>
          We don't sell your data. We don't share it with advertisers. We don't build a
          profile of you to target ads with. We don't have a marketing department asking
          for any of this.
        </L>
        <L>We use your email to send you the things you signed up for. That's it.</L>
      </LegalSection>

      <LegalSection label="YOUR RIGHTS">
        <L>Under UK GDPR you can:</L>
        <LList
          items={[
            "See what data we hold about you.",
            "Ask us to correct anything that's wrong.",
            "Ask us to delete your account and the data attached to it.",
            "Object to how we use your data.",
            "Take your data with you if you'd like.",
          ]}
        />
        <L>
          To do any of these, email hello@whoknew.guide. We'll respond within 30 days,
          usually faster.
        </L>
      </LegalSection>

      <LegalSection label="COOKIES">
        <L>We use cookies for two things:</L>
        <LList
          items={[
            "Keeping you logged in.",
            "Anonymous analytics to understand how the site is used.",
          ]}
        />
        <L>
          We don't use advertising cookies. We don't share cookie data with third
          parties. When the law requires us to ask, we ask properly.
        </L>
      </LegalSection>

      <LegalSection label="THIRD PARTIES WE USE">
        <L>We use a small number of trusted services to run this site:</L>
        <LList
          items={[
            "Stripe (payments)",
            "Supabase (account database and file storage)",
            "Mux (video hosting, where applicable)",
            "Resend (email delivery, where applicable)",
          ]}
        />
        <L>Each has their own privacy policy. All are GDPR-compliant.</L>
      </LegalSection>

      <LegalSection label="CHANGES TO THIS POLICY">
        <L>
          If we change this, we'll update the date at the top and tell existing customers
          by email. We won't quietly add anything that takes more of your data than you
          originally agreed to.
        </L>
        <LegalAside>
          If you have a question this page doesn't answer, just ask. We don't have a
          legal team. You'll be writing to a real person.
        </LegalAside>
      </LegalSection>
    </LegalPage>
  );
}
